// PR-10.1 — admin UI for managing league_rounds (chained rounds within
// season). Mounted in a "Omgångar" tab inside LeagueForm. Out of scope
// for this PR: player-facing rounds view, auto-status cron — see PR-10.2.
//
// Design choices:
//   - One mutation per round op (create / update / delete) — simple,
//     reuses react-query invalidation. No bulk-edit UX yet; admin
//     manages 4-8 rounds typically, not 50.
//   - Inline-edit row UI rather than a modal dialog — fewer clicks,
//     fits the "admin puzzles dates" workflow. Save / Cancel per row.
//   - Validation: the pure helper validateRoundDates() runs over the
//     full set on every save attempt — catches gaps + overlaps + bad
//     date ranges. DB CHECKs are belt-and-braces (start_date <=
//     end_date, UNIQUE(league_id, round_number)).
//   - Status: shows the *natural* status from computeRoundStatus(today)
//     alongside the stored status; admin can override but the cron in
//     PR-10.2 will resync over time.
//   - opens_after chain: optional Select per round to pick a
//     prerequisite. Helper isRoundOpen() is the canonical predicate
//     (used by player view in PR-10.2).
//
// Casts: `from("league_rounds" as any)` because the supabase generated
// types haven't been regenerated for this PR (the file is too large to
// inline-update via MCP). Same pattern as `league_team_preferences as any`
// elsewhere in the codebase.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  validateRoundDates,
  computeRoundStatus,
  type LeagueRoundLite,
} from "@/lib/leagueRoundsHelpers";
// TASK-73 Phase 1 PR-B: per-omgång match generation + R4 guard for delete.
import {
  generateMatchesForRound,
  type GenerateMatchesForRoundResult,
} from "@/lib/leagueScheduling";
import { buildUnplacedReasonsDescription } from "@/lib/buildUnplacedReasonsDescription";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
// UPPDRAG-53: round start/end dates use the holiday-aware picker so admins
// can plan around Långfredag, Midsommarafton, etc.
import { DatePickerWithHolidays } from "@/components/ui/date-picker-with-holidays";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
// TASK-73 Phase 1 PR-B: Sheet for the omgång detail view (matches list +
// generate/delete buttons).
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Loader2, Plus, Pencil, Trash2, Check, X, Link as LinkIcon, ListChecks, Sparkles } from "lucide-react";
import { toast } from "sonner";

interface LeagueRoundRow {
  id: string;
  league_id: string;
  round_number: number;
  name: string | null;
  start_date: string;
  end_date: string;
  status: "upcoming" | "active" | "completed";
  opens_after_round_id: string | null;
  created_at: string;
  updated_at: string;
}

interface DraftRound {
  round_number: number;
  name: string;
  start_date: string;
  end_date: string;
  opens_after_round_id: string | null;
}

const today = (): string => new Date().toISOString().slice(0, 10);

const STATUS_VARIANT: Record<LeagueRoundRow["status"], "secondary" | "default" | "outline"> = {
  upcoming: "outline",
  active: "default",
  completed: "secondary",
};

export default function LeagueRoundsManager({ leagueId }: { leagueId: string }) {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftRound | null>(null);
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<DraftRound>(emptyDraft(1));
  // TASK-73 Phase 1 PR-B: omgång detail view (matches list + generate +
  // delete). selectedRoundId drives the Sheet's open state and the
  // query-key for fetching this round's matches.
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);

  const { data: rounds = [], isLoading } = useQuery<LeagueRoundRow[]>({
    queryKey: ["league-rounds", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_rounds" as never)
        .select("id, league_id, round_number, name, start_date, end_date, status, opens_after_round_id, created_at, updated_at")
        .eq("league_id", leagueId)
        .order("round_number", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as LeagueRoundRow[];
    },
  });

  const nextRoundNumber = rounds.length > 0
    ? Math.max(...rounds.map((r) => r.round_number)) + 1
    : 1;

  const createMutation = useMutation({
    mutationFn: async (draft: DraftRound) => {
      // Re-validate the new draft together with the existing rounds. This
      // prevents the "save 2 then save 3 with overlap" race that the DB
      // CHECK alone wouldn't catch (CHECKs only see one row at a time).
      const merged: LeagueRoundLite[] = [
        ...rounds.map((r) => ({
          round_number: r.round_number,
          start_date: r.start_date,
          end_date: r.end_date,
        })),
        {
          round_number: draft.round_number,
          start_date: draft.start_date,
          end_date: draft.end_date,
        },
      ];
      const validationErr = validateRoundDates(merged);
      if (validationErr) throw new Error(validationErr);

      const { error } = await supabase.from("league_rounds" as never).insert({
        league_id: leagueId,
        round_number: draft.round_number,
        name: draft.name.trim() || null,
        start_date: draft.start_date,
        end_date: draft.end_date,
        opens_after_round_id: draft.opens_after_round_id,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("leagues.rounds.created", "Omgång skapad"));
      qc.invalidateQueries({ queryKey: ["league-rounds", leagueId] });
      setAdding(false);
      setAddDraft(emptyDraft(nextRoundNumber + 1));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: DraftRound }) => {
      // Validate the new value against all other rounds (excluding the
      // current one being edited)
      const merged: LeagueRoundLite[] = [
        ...rounds
          .filter((r) => r.id !== id)
          .map((r) => ({
            round_number: r.round_number,
            start_date: r.start_date,
            end_date: r.end_date,
          })),
        {
          round_number: draft.round_number,
          start_date: draft.start_date,
          end_date: draft.end_date,
        },
      ];
      const validationErr = validateRoundDates(merged);
      if (validationErr) throw new Error(validationErr);

      const { error } = await supabase
        .from("league_rounds" as never)
        .update({
          round_number: draft.round_number,
          name: draft.name.trim() || null,
          start_date: draft.start_date,
          end_date: draft.end_date,
          opens_after_round_id: draft.opens_after_round_id,
        } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("leagues.rounds.updated", "Omgång uppdaterad"));
      qc.invalidateQueries({ queryKey: ["league-rounds", leagueId] });
      setEditingId(null);
      setEditDraft(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("league_rounds" as never).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("leagues.rounds.deleted", "Omgång borttagen"));
      qc.invalidateQueries({ queryKey: ["league-rounds", leagueId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ─── TASK-73 Phase 1 PR-B: matches for the selected round ──────────────
  //
  // Embed shape mirrors the data the detail Sheet needs to render the
  // matches table (home/away team names, division name, scheduled
  // date/time/court). We don't denormalize team names into league_matches
  // — PostgREST embed via teams!home_team_id_fkey + teams!away_team_id_fkey.

  type MatchRowForRound = {
    id: string;
    round: number;
    status: string;
    division_id: string | null;
    home_team_id: string;
    away_team_id: string;
    scheduled_date: string | null;
    scheduled_time: string | null;
    court_id: string | null;
    booking_id: string | null;
    home_team: { team_name: string | null } | null;
    away_team: { team_name: string | null } | null;
    division: { name: string | null } | null;
    court: { name: string | null } | null;
  };

  const matchesQuery = useQuery<MatchRowForRound[]>({
    queryKey: ["league-round-matches", selectedRoundId],
    queryFn: async () => {
      if (!selectedRoundId) return [];
      const { data, error } = await supabase
        .from("league_matches")
        .select(`
          id, round, status, division_id, home_team_id, away_team_id,
          scheduled_date, scheduled_time, court_id, booking_id,
          home_team:teams!league_matches_home_team_id_fkey(team_name),
          away_team:teams!league_matches_away_team_id_fkey(team_name),
          division:divisions(name),
          court:courts(name)
        `)
        .eq("round_id", selectedRoundId)
        .order("round", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as MatchRowForRound[];
    },
    enabled: !!selectedRoundId,
  });

  const selectedRound = selectedRoundId ? rounds.find((r) => r.id === selectedRoundId) ?? null : null;

  const generateMatchesMutation = useMutation({
    mutationFn: async (roundId: string) => {
      const result: GenerateMatchesForRoundResult = await generateMatchesForRound(supabase, {
        roundId,
        leagueId,
        // skipIfMatchesExist=false: admin explicitly clicked "Generera
        // matcher". The button only renders when the matches list is
        // empty so collision with existing matches is impossible in
        // normal flow. Defensive: helper still per-division skips.
        skipIfMatchesExist: false,
      });
      return result;
    },
    onSuccess: (result) => {
      const placedFraction = `${result.matchesPlaced}/${result.matchesCreated}`;
      if (result.matchesCreated === 0) {
        toast.warning(
          t(
            "leagues.rounds.generateNoMatches",
            "Inga matcher skapades — kontrollera att divisionerna har minst 2 lag.",
          ),
        );
      } else if (result.matchesUnplaced > 0) {
        // Path D: surface per-match reasons in the toast description so
        // admin sees which specific matches couldn't be placed and why,
        // instead of just an aggregate count. Cap at 5 + "...and N more"
        // for cases where every match in a division got stuck (e.g. when
        // every Speltider slot in that weekday is booked).
        toast.success(
          t(
            "leagues.rounds.generatePartial",
            "Skapade {{created}} matcher i {{divs}} division(er). Placerade {{placed}} — {{unplaced}} oplacerade (kontrollera Speltider).",
            {
              created: result.matchesCreated,
              divs: result.divisionsProcessed,
              placed: placedFraction,
              unplaced: result.matchesUnplaced,
            },
          ),
          {
            description: buildUnplacedReasonsDescription({
              reasons: result.unplacedReasons,
              t,
              maxItems: 5,
            }),
          },
        );
      } else {
        toast.success(
          t(
            "leagues.rounds.generateSuccess",
            "Skapade {{created}} matcher i {{divs}} division(er) — alla placerade.",
            {
              created: result.matchesCreated,
              divs: result.divisionsProcessed,
            },
          ),
        );
      }
      qc.invalidateQueries({ queryKey: ["league-round-matches", selectedRoundId] });
      qc.invalidateQueries({ queryKey: ["league-matches"] }); // global match queries
    },
    onError: (err: Error) =>
      toast.error(err.message || t("leagues.rounds.generateError", "Kunde inte generera matcher")),
  });

  const deleteMatchesMutation = useMutation({
    mutationFn: async (roundId: string) => {
      // R4 guard: refuse if any match in this round has a booking_id.
      // Admin must avboka first or contact support (per the brief).
      const { count: bookingLinkedCount, error: cntErr } = await supabase
        .from("league_matches")
        .select("id", { count: "exact", head: true })
        .eq("round_id", roundId)
        .not("booking_id", "is", null);
      if (cntErr) throw cntErr;
      if ((bookingLinkedCount ?? 0) > 0) {
        throw new Error(
          t(
            "leagues.rounds.bookingsLinkedRefusal",
            "{{n}} matcher har bokningar kopplade. Avboka dem först eller kontakta support.",
            { n: bookingLinkedCount },
          ),
        );
      }
      // Safe to bulk DELETE. FK CASCADE on dependent rows
      // (match_results, league_match_messages, etc — schema-level concern)
      // handles cleanup.
      const { error: delErr } = await supabase
        .from("league_matches")
        .delete()
        .eq("round_id", roundId);
      if (delErr) throw delErr;
    },
    onSuccess: () => {
      toast.success(
        t("leagues.rounds.deleteMatchesSuccess", "Matcher borttagna"),
      );
      qc.invalidateQueries({ queryKey: ["league-round-matches", selectedRoundId] });
      qc.invalidateQueries({ queryKey: ["league-matches"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const startEdit = (r: LeagueRoundRow) => {
    setEditingId(r.id);
    setEditDraft({
      round_number: r.round_number,
      name: r.name ?? "",
      start_date: r.start_date,
      end_date: r.end_date,
      opens_after_round_id: r.opens_after_round_id,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const startAdd = () => {
    setAdding(true);
    setAddDraft(emptyDraft(nextRoundNumber));
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddDraft(emptyDraft(nextRoundNumber));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            {t("leagues.rounds.title", "Omgångar")}{" "}
            <span className="text-sm text-muted-foreground font-normal">
              ({rounds.length})
            </span>
          </h2>
          <p className="text-xs text-muted-foreground">
            {t(
              "leagues.rounds.subtitle",
              "Definiera datumintervall för varje omgång. Kedjan styrs via 'Öppnas efter' — nästa omgång låses upp när föregående är klar.",
            )}
          </p>
        </div>
        {!adding && (
          <Button size="sm" onClick={startAdd} className="gap-1">
            <Plus className="h-4 w-4" />
            {t("leagues.rounds.add", "Lägg till omgång")}
          </Button>
        )}
      </div>

      {/* Add row */}
      {adding && (
        <Card className="border-primary/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">
              {t("leagues.rounds.addNew", "Ny omgång")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RoundFormFields
              draft={addDraft}
              onChange={setAddDraft}
              otherRounds={rounds}
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={cancelAdd}>
                <X className="h-4 w-4 mr-1" /> {t("common.cancel", "Avbryt")}
              </Button>
              <Button
                size="sm"
                onClick={() => createMutation.mutate(addDraft)}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                <Check className="h-4 w-4 mr-1" />
                {t("leagues.rounds.save", "Spara")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing rounds list */}
      {rounds.length === 0 && !adding ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            {t("leagues.rounds.empty", "Inga omgångar definierade än")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rounds.map((r) => {
            const naturalStatus = computeRoundStatus(r, today());
            const stored = r.status;
            const statusOutOfSync = naturalStatus !== stored;
            const prereq = r.opens_after_round_id
              ? rounds.find((other) => other.id === r.opens_after_round_id)
              : null;

            return (
              <Card key={r.id}>
                <CardContent className="py-3">
                  {editingId === r.id && editDraft ? (
                    <>
                      <RoundFormFields
                        draft={editDraft}
                        onChange={setEditDraft as (d: DraftRound) => void}
                        otherRounds={rounds.filter((other) => other.id !== r.id)}
                      />
                      <div className="flex justify-end gap-2 mt-3">
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          <X className="h-4 w-4 mr-1" /> {t("common.cancel", "Avbryt")}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updateMutation.mutate({ id: r.id, draft: editDraft })}
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                          <Check className="h-4 w-4 mr-1" />
                          {t("leagues.rounds.save", "Spara")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">
                            #{r.round_number}
                          </span>
                          {r.name && (
                            <span className="text-sm text-muted-foreground">
                              {r.name}
                            </span>
                          )}
                          <Badge variant={STATUS_VARIANT[stored]} className="text-xs">
                            {t(`leagues.rounds.status.${stored}`, stored)}
                          </Badge>
                          {statusOutOfSync && (
                            <Badge variant="outline" className="text-[10px] text-amber-600 dark:text-amber-400">
                              {t("leagues.rounds.naturalLabel", "naturlig:")} {t(`leagues.rounds.status.${naturalStatus}`, naturalStatus)}
                            </Badge>
                          )}
                          {prereq && (
                            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                              <LinkIcon className="h-3 w-3" />
                              {t("leagues.rounds.opensAfterShort", "efter")} #{prereq.round_number}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {r.start_date} – {r.end_date}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {/* TASK-73 Phase 1 PR-B: open round detail Sheet
                            (matches list + Generera/Ta bort buttons). */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 gap-1 text-xs"
                          onClick={() => setSelectedRoundId(r.id)}
                          title={t("leagues.rounds.viewMatches", "Visa matcher")}
                        >
                          <ListChecks className="h-3.5 w-3.5" />
                          {t("leagues.rounds.matches", "Matcher")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => startEdit(r)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t("leagues.rounds.deleteTitle", "Ta bort omgång")} #{r.round_number}?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t(
                                  "leagues.rounds.deleteBody",
                                  "Denna åtgärd kan inte ångras. Om andra omgångar har 'Öppnas efter' = denna, kommer den länken att rensas (FK SET NULL).",
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("common.cancel", "Avbryt")}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(r.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {t("leagues.rounds.delete", "Ta bort")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* TASK-73 Phase 1 PR-B: omgång detail Sheet (matches list + buttons) */}
      <Sheet open={!!selectedRoundId} onOpenChange={(open) => { if (!open) setSelectedRoundId(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedRound && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  #{selectedRound.round_number}
                  {selectedRound.name && <span className="text-muted-foreground font-normal">— {selectedRound.name}</span>}
                  <Badge variant={STATUS_VARIANT[selectedRound.status]} className="text-xs ml-2">
                    {t(`leagues.rounds.status.${selectedRound.status}`, selectedRound.status)}
                  </Badge>
                </SheetTitle>
                <SheetDescription>
                  {selectedRound.start_date} – {selectedRound.end_date}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-3">
                {matchesQuery.isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (matchesQuery.data ?? []).length === 0 ? (
                  // Empty state — Generera matcher CTA
                  <Card>
                    <CardContent className="py-12 text-center space-y-4">
                      <div className="text-sm text-muted-foreground">
                        {t(
                          "leagues.rounds.matchesEmptyState",
                          "Inga matcher genererade för denna omgång än.",
                        )}
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            disabled={generateMatchesMutation.isPending}
                            className="gap-2"
                          >
                            {generateMatchesMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4" />
                            )}
                            {t("leagues.rounds.generateMatches", "Generera matcher")}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t("leagues.rounds.generateConfirmTitle", "Generera matcher för omgång #{{n}}?", { n: selectedRound.round_number })}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {t(
                                "leagues.rounds.generateConfirmBody",
                                "Skapar Berger round-robin för varje division (en match per lag-par) och försöker auto-placera dem på speltider mellan {{start}} och {{end}}.",
                                { start: selectedRound.start_date, end: selectedRound.end_date },
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("common.cancel", "Avbryt")}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => generateMatchesMutation.mutate(selectedRound.id)}
                            >
                              {t("leagues.rounds.generateMatches", "Generera matcher")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </CardContent>
                  </Card>
                ) : (
                  // Matches table — grouped by division
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">
                        {t("leagues.rounds.matches", "Matcher")}{" "}
                        <span className="text-xs text-muted-foreground font-normal">({(matchesQuery.data ?? []).length})</span>
                      </h3>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1 text-destructive hover:text-destructive"
                            disabled={deleteMatchesMutation.isPending}
                          >
                            {deleteMatchesMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                            {t("leagues.rounds.deleteMatches", "Ta bort matcher")}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t("leagues.rounds.deleteMatchesConfirmTitle", "Ta bort {{n}} matcher?", { n: (matchesQuery.data ?? []).length })}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {t(
                                "leagues.rounds.deleteMatchesConfirmBody",
                                "Detta är permanent. Om någon match har en bokning kopplad kommer åtgärden att avbrytas — avboka först eller kontakta support.",
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("common.cancel", "Avbryt")}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMatchesMutation.mutate(selectedRound.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {t("leagues.rounds.deleteMatches", "Ta bort matcher")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {/* Group matches by division for readable scan */}
                    {(() => {
                      const matches = matchesQuery.data ?? [];
                      const byDiv = new Map<string, MatchRowForRound[]>();
                      for (const m of matches) {
                        const key = m.division?.name ?? m.division_id ?? "—";
                        if (!byDiv.has(key)) byDiv.set(key, []);
                        byDiv.get(key)!.push(m);
                      }
                      return Array.from(byDiv.entries()).map(([divName, divMatches]) => (
                        <Card key={divName}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">
                              {divName} <span className="text-xs text-muted-foreground font-normal">({divMatches.length})</span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-1.5">
                              {divMatches.map((m) => (
                                <div key={m.id} className="grid grid-cols-12 gap-2 text-xs items-center py-1 border-b last:border-b-0">
                                  <span className="col-span-1 font-mono text-muted-foreground">v{m.round}</span>
                                  <span className="col-span-3 truncate">{m.home_team?.team_name ?? "?"}</span>
                                  <span className="col-span-1 text-center text-muted-foreground">vs</span>
                                  <span className="col-span-3 truncate">{m.away_team?.team_name ?? "?"}</span>
                                  <span className="col-span-2 truncate text-muted-foreground">
                                    {m.scheduled_date && m.scheduled_time
                                      ? `${m.scheduled_date} ${(m.scheduled_time as string).slice(0, 5)}`
                                      : t("leagues.rounds.unplaced", "oplacerad")}
                                  </span>
                                  <span className="col-span-2 truncate text-muted-foreground">
                                    {m.court?.name ?? "—"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ));
                    })()}
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── RoundFormFields (shared between Add + Edit) ───────────────────────

function RoundFormFields({
  draft,
  onChange,
  otherRounds,
}: {
  draft: DraftRound;
  onChange: (d: DraftRound) => void;
  otherRounds: LeagueRoundRow[];
}) {
  const { t } = useTranslation("admin");
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">{t("leagues.rounds.roundNumber", "Omgångsnummer")}</Label>
        <Input
          type="number"
          min={1}
          value={draft.round_number}
          onChange={(e) =>
            onChange({ ...draft, round_number: parseInt(e.target.value, 10) || 1 })
          }
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("leagues.rounds.name", "Namn (valfritt)")}</Label>
        <Input
          value={draft.name}
          placeholder={t("leagues.rounds.namePlaceholder", "ex. Höstomgång")}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
        />
      </div>
      {/* UPPDRAG-53: round-level dates swapped to holiday-aware picker.
          Admins planning omgångar that span Påsk / Midsommar / Jul see
          the red days inline and can avoid them at a glance. */}
      <div className="space-y-1">
        <Label className="text-xs">{t("leagues.rounds.startDate", "Startdatum")}</Label>
        <DatePickerWithHolidays
          value={draft.start_date}
          onChange={(iso) => onChange({ ...draft, start_date: iso })}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("leagues.rounds.endDate", "Slutdatum")}</Label>
        <DatePickerWithHolidays
          value={draft.end_date}
          onChange={(iso) => onChange({ ...draft, end_date: iso })}
        />
      </div>
      <div className="space-y-1 col-span-2">
        <Label className="text-xs">
          {t("leagues.rounds.opensAfterLabel", "Öppnas efter (valfritt — kedjelogik)")}
        </Label>
        <Select
          value={draft.opens_after_round_id ?? "__none__"}
          onValueChange={(v) =>
            onChange({ ...draft, opens_after_round_id: v === "__none__" ? null : v })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder={t("leagues.rounds.opensAfterPlaceholder", "Ingen")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t("leagues.rounds.opensAfterNone", "Ingen (datumstyrd)")}</SelectItem>
            {otherRounds
              .filter((r) => r.round_number !== draft.round_number)
              .map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  #{r.round_number}
                  {r.name ? ` — ${r.name}` : ""}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function emptyDraft(roundNumber: number): DraftRound {
  return {
    round_number: roundNumber,
    name: "",
    start_date: "",
    end_date: "",
    opens_after_round_id: null,
  };
}
